import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  effect,
  computed,
  OnInit
} from '@angular/core';
import { CommonModule, DecimalPipe, DatePipe, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SubscriptionService, SubscriptionStatus } from '../../services/subscription.service';
import { CountriesService } from '../../services/countries.service';
import { ToastService } from '../../services/toast.service';
import { FooterComponent } from '../footer/footer.component';
import { CheckoutComponent } from '../checkout/checkout.component';

@Component({
  selector: 'app-subscription-management',
  standalone: true,
  imports: [CommonModule, DecimalPipe, FormsModule, FooterComponent, CheckoutComponent],
  template: `
    <main>
      <div class="header-container">
        <div class="back-link">
          <a (click)="goBack()" style="cursor: pointer; color: oklch(51% 0.28 263.83); font-size: 0.875rem;">← Zurück</a>
        </div>
        <div class="header-content">
          <a (click)="goHome()" style="cursor: pointer; text-decoration: none; display: inline-block;">
            <h1>ProTyco</h1>
            <p style="font-size: 0.875rem; text-align: center; margin: 0.25rem 0 0 0;">
              <span style="color: oklch(51% 0.28 263.83);">PRO</span>PERTY <span style="color: oklch(51% 0.28 263.83);">TYCO</span>ON
            </p>
          </a>
        </div>
      </div>

      <div class="subscription-scroll">
      <div class="subscription-container">        
        <h2 class="form-title">Dein Abonnement</h2>

        @if (isLoading()) {
          <p class="info-text">Lädt Abonnement-Informationen...</p>
        } @else if (subscriptionStatus()) {
          <div class="subscription-info">
            <!-- AKTUELLER PLAN -->
            <div class="info-section">
              <h3>📋 Aktueller Plan</h3>
              <div class="info-row">
                <span class="label">Plan-Name:</span>
                <span class="value">{{ subscriptionStatus()!.planName || 'Kein aktiver Plan' }}</span>
              </div>

              @if (subscriptionStatus()!.status === 'active') {
                <div class="info-row">
                  <span class="label">Aktiv seit:</span>
                  <span class="value">{{ getActiveSince() }}</span>
                </div>
              }

              <div class="info-row">
                <span class="label">Status:</span>
                <span class="value" [ngClass]="'status-' + subscriptionStatus()!.status">
                  @if (subscriptionStatus()!.status === 'active') {
                    ✅ Aktiv
                  } @else if (subscriptionStatus()!.status === 'expired') {
                    ❌ Abgelaufen
                  } @else {
                    ⭕ Inaktiv
                  }
                </span>
              </div>
            </div>

            <!-- VERBLEIBENDE ZEIT -->
            <div class="info-section">
              <h3>⏰ Verbleibende Zeit</h3>

              @if (subscriptionStatus()!.status === 'active' && subscriptionStatus()!.endDate) {
                <div class="info-row">
                  <span class="label">Verfällt am:</span>
                  <span class="value">{{ getEndDate() }}</span>
                </div>

                <div class="info-row">
                  <span class="label">Verbleibend:</span>
                  <span class="value" [ngClass]="{ 'warning': subscriptionStatus()!.daysRemaining <= 7 }">
                    {{ subscriptionStatus()!.daysRemaining }} Tage
                    @if (subscriptionStatus()!.isExpiringsSoon) {
                      <span class="warning-badge">⚠️</span>
                    }
                  </span>
                </div>
              } @else if (subscriptionStatus()!.endDate) {
                <div class="info-row">
                  <span class="label">Abgelaufen am:</span>
                  <span class="value">{{ getEndDate() }}</span>
                </div>
              } @else {
                <div class="info-row">
                  <span class="value">Keine aktiven oder abgelaufenen Abonnements</span>
                </div>
              }
            </div>

            <!-- ZAHLUNGS-DETAILS -->
            @if (paymentDetails()) {
              <div class="info-section">
                <h3>💰 Zahlungs-Details</h3>
                <div class="info-row">
                  <span class="label">Preis:</span>
                  <span class="value">€ {{ paymentDetails()!.amount | number: '1.2-2' }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Zahl-Provider:</span>
                  <span class="value">{{ getProviderDisplayName(paymentDetails()!.provider) }}</span>
                </div>
              </div>
            }

            <!-- ACTIONS -->
            <div class="actions-section">
              <div class="plan-selection-row">
                <select class="plan-dropdown" [value]="selectedPlanId()?.toString() || ''" (change)="onPlanSelected($event)">
                  <option value="">-- Paket wählen --</option>
                  @for (plan of availablePlans(); track plan.planId) {
                    <option [value]="plan.planId.toString()" [selected]="plan.planId === selectedPlanId()">{{ plan.name }} - €{{ plan.priceEUR | number: '1.2-2' }}</option>
                  }
                </select>
                <button
                  class="auth-button primary-button renew-button"
                  (click)="onRenewSubscription()"
                  [disabled]="isCheckoutLoading() || !selectedPlanId()"
                >
                  @if (isCheckoutLoading()) {
                    Wird verarbeitet...
                  } @else {
                    Jetzt verlängern
                  }
                </button>
              </div>

              <a
                class="payment-history-link"
                (click)="onViewPaymentHistory()"
              >
                Zahlungshistorie & Rechnungen
              </a>
            </div>

            <!-- CHECKOUT MODAL -->
            @if (showCheckout()) {
              <app-checkout
                [plan]="selectedPlan()"
                [visible]="showCheckout()"
                (closed)="closeCheckout()"
              ></app-checkout>
            }

            <!-- PAYMENT HISTORY MODAL -->
            @if (showPaymentHistory()) {
              <div class="modal-overlay"></div>
              <div class="modal-content">
                <div class="modal-header">
                  <h2>📝 Zahlungshistorie & Rechnungen</h2>
                  <button class="modal-close" (click)="closePaymentHistory()" title="Schließen">✕</button>
                </div>
                <div class="modal-body">
                  <!-- RECHNUNGSDATEN SEKTION -->
                  <div class="billing-data-section">
                    <button class="accordion-header" (click)="rechnungsdatenOpen.set(!rechnungsdatenOpen())">
                      <span class="accordion-arrow" [class.open]="rechnungsdatenOpen()">▶</span>
                      <span>📋 Rechnungsdaten</span>
                    </button>
                    @if (rechnungsdatenOpen()) {
                      @if (!editingBillingData()) {
                        <div class="billing-display accordion-content">
                          <div class="bill-row">
                            <div class="bill-label">Kundennummer</div>
                            <div class="bill-value">{{ subscriptionStatus()?.customerId || '-' }}</div>
                          </div>
                          @if (subscriptionStatus()?.isB2B) {
                            <div class="bill-row">
                              <div class="bill-label">Unternehmensname</div>
                              <div class="bill-value">{{ subscriptionStatus()?.companyName || '-' }}</div>
                            </div>
                          }
                          <div class="bill-row">
                            <div class="bill-label">Vorname</div>
                            <div class="bill-value">{{ subscriptionStatus()?.firstName || '-' }}</div>
                          </div>
                          <div class="bill-row">
                            <div class="bill-label">Nachname</div>
                            <div class="bill-value">{{ subscriptionStatus()?.lastName || '-' }}</div>
                          </div>
                          @if (subscriptionStatus()?.isB2B && subscriptionStatus()?.customerVAT) {
                            <div class="bill-row">
                              <div class="bill-label">USt-Id</div>
                              <div class="bill-value">{{ subscriptionStatus()?.customerVAT }}</div>
                            </div>
                          }
                          <div class="bill-row">
                            <div class="bill-label">Straße</div>
                            <div class="bill-value">{{ subscriptionStatus()?.street || '-' }}</div>
                          </div>
                          <div class="bill-row">
                            <div class="bill-label">PLZ</div>
                            <div class="bill-value">{{ subscriptionStatus()?.postalCode || '-' }}</div>
                          </div>
                          <div class="bill-row">
                            <div class="bill-label">Stadt</div>
                            <div class="bill-value">{{ subscriptionStatus()?.city || '-' }}</div>
                          </div>
                          <div class="bill-row">
                            <div class="bill-label">Land</div>
                            <div class="bill-value">{{ subscriptionStatus()?.country || 'Deutschland' }}</div>
                          </div>
                          <button class="auth-button primary-button" (click)="startEditingBillingData()" style="margin-top: 16px;">
                            ✏️ Bearbeiten
                          </button>
                        </div>
                      } @else if (editingBillingData()) {
                      <div class="billing-form">
                        <!-- BUSINESS/PERSONAL TOGGLE WITH CUSTOMER NUMBER -->
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
                          <div style="padding: 6px 12px; border-radius: 4px; font-size: 12px; color: #666;">
                            <strong>Kundennummer:</strong> {{ subscriptionStatus()?.customerId || '-' }}
                          </div>
                          <label class="checkbox-label" style="margin: 0;">
                            <input 
                              type="checkbox" 
                              [checked]="subscriptionStatus()?.isB2B || false"
                              disabled
                              class="checkbox-input checkbox-disabled"
                            />
                            <span>Geschäftliche Nutzung</span>
                          </label>
                        </div>

                        <!-- HELPER TEXT -->
                        <div class="validation-helper-box">
                          <span class="helper-icon">ℹ️</span>
                          <div class="helper-text">
                            <div><strong>Bitte füllen Sie aus.</strong></div>
                            <div><strong>{{ helperText() }}</strong></div>
                          </div>
                        </div>

                        <!-- BUSINESS INFO (B2B) FIRST -->
                        @if (shouldShowBusinessInfo()) {
                          <div class="form-group">
                            <label>Unternehmensname <span class="conditional-required">*</span></label>
                            <input 
                              type="text" 
                              [value]="billingDataForm().companyName"
                              (input)="updateFormField('companyName', $event)"
                              maxlength="100"
                              placeholder="z.B. Meine Firma GmbH"
                            />
                            <div class="char-counter" [class.warning]="companyNameCount() >= maxLengths.companyName">
                              {{ companyNameCount() }}/{{ maxLengths.companyName }}
                            </div>
                          </div>
                        }

                        <!-- PERSONAL INFO (ALWAYS SHOWN) -->
                        <div class="form-row">
                          <div class="form-group" style="flex: 1;">
                            <label>Vorname @if (!subscriptionStatus()?.isB2B) { <span class="conditional-required">*</span> }</label>
                            <input 
                              type="text" 
                              [value]="billingDataForm().firstName"
                              (input)="updateFormField('firstName', $event)"
                              maxlength="50"
                              placeholder="z.B. Max"
                            />
                            <div class="char-counter" [class.warning]="firstNameCount() >= maxLengths.firstName">
                              {{ firstNameCount() }}/{{ maxLengths.firstName }}
                            </div>
                          </div>
                          <div class="form-group" style="flex: 1;">
                            <label>Nachname @if (!subscriptionStatus()?.isB2B) { <span class="conditional-required">*</span> }</label>
                            <input 
                              type="text" 
                              [value]="billingDataForm().lastName"
                              (input)="updateFormField('lastName', $event)"
                              maxlength="50"
                              placeholder="z.B. Mustermann"
                            />
                            <div class="char-counter" [class.warning]="lastNameCount() >= maxLengths.lastName">
                              {{ lastNameCount() }}/{{ maxLengths.lastName }}
                            </div>
                          </div>
                        </div>

                        <!-- B2B VAT -->
                        @if (shouldShowBusinessInfo()) {
                          <div class="form-group">
                            <label>USt-Id <span class="conditional-required">*</span></label>
                            <input 
                              type="text" 
                              [value]="billingDataForm().customerVAT"
                              (input)="updateFormField('customerVAT', $event)"
                              maxlength="50"
                              placeholder="z.B. DE123456789"
                            />
                            <div class="char-counter" [class.warning]="customerVATCount() >= maxLengths.customerVAT">
                              {{ customerVATCount() }}/{{ maxLengths.customerVAT }}
                            </div>
                          </div>
                        }

                        <!-- ADDRESS INFO (FOR ALL) -->
                        <div class="form-group">
                          <label>Straße & Hausnummer <span class="conditional-required">*</span></label>
                          <input 
                            type="text" 
                            [value]="billingDataForm().street"
                            (input)="updateFormField('street', $event)"
                            maxlength="100"
                            placeholder="z.B. Hauptstraße 123"
                          />
                          <div class="char-counter" [class.warning]="streetCount() >= maxLengths.street">
                            {{ streetCount() }}/{{ maxLengths.street }}
                          </div>
                        </div>
                        <div class="form-row">
                          <div class="form-group plz-group">
                            <label>PLZ <span class="conditional-required">*</span></label>
                            <input 
                              type="text" 
                              [value]="billingDataForm().postalCode"
                              (input)="updateFormField('postalCode', $event)"
                              maxlength="16"
                              placeholder="z.B. 12345"
                            />
                            <div class="char-counter" [class.warning]="postalCodeCount() >= maxLengths.postalCode">
                              {{ postalCodeCount() }}/{{ maxLengths.postalCode }}
                            </div>
                          </div>
                          <div class="form-group stadt-group" style="flex: 0.95;">
                            <label>Stadt <span class="conditional-required">*</span></label>
                            <input 
                              type="text" 
                              [value]="billingDataForm().city"
                              (input)="updateFormField('city', $event)"
                              maxlength="100"
                              placeholder="z.B. Berlin"
                            />
                            <div class="char-counter" [class.warning]="cityCount() >= maxLengths.city">
                              {{ cityCount() }}/{{ maxLengths.city }}
                            </div>
                          </div>
                          <div class="form-group" style="flex: 1;">
                            <label>Land <span class="conditional-required">*</span></label>
                            <input 
                              type="text"
                              [value]="billingDataForm().country || 'Deutschland'"
                              readonly
                              class="readonly-input"
                            />
                          </div>
                        </div>

                        <!-- VALIDATION ERROR MESSAGE -->
                        @if (validationError()) {
                          <div class="validation-error-box">
                            <span class="error-icon">⚠️</span>
                            <span class="error-text">{{ validationError() }}</span>
                          </div>
                        }

                        <div class="form-buttons">
                          <button 
                            class="auth-button primary-button" 
                            (click)="saveBillingData()"
                            [disabled]="!isSaveEnabled()"
                            [title]="!isSaveEnabled() ? (validationError() || 'Bitte füllen Sie erforderliche Felder aus') : 'Speichern'"
                          >
                            💾 Speichern
                          </button>
                          <button class="auth-button back-button" (click)="cancelEditingBillingData()">
                            ✕ Abbrechen
                          </button>
                        </div>
                      </div>
                    }
                    }
                  </div>

                  <!-- ZAHLUNGSTABELLE -->
                  <h3 style="margin-top: 2rem;">Zahlungen</h3>
                  @if (paymentHistory() && paymentHistory().length > 0) {
                    <div class="payment-table">
                      @for (payment of paymentHistory(); track payment.paymentId) {
                        <div class="payment-row">
                          <div class="payment-info">
                            <div class="payment-plan">Immobilien-Amortisations-Rechner Abonnement - {{ payment.planName }}</div>
                            <div class="payment-date">{{ getPaymentDate(payment.createdAt) }}</div>
                          </div>
                          <div class="payment-amount">€ {{ payment.amount | number: '1.2-2' }}</div>
                          <div class="payment-buttons">
                            <button class="download-button" (click)="downloadInvoice(payment)" title="Rechnung herunterladen">
                              📄 PDF
                            </button>
                            @if (subscriptionStatus()?.isB2B) {
                              <button class="download-button xml-button" (click)="downloadXmlInvoice(payment)" title="E-Rechnung (XML - ZUGFeRD 2.4)">
                                📋 XML
                              </button>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  } @else {
                    <p class="info-text">Keine Zahlungen vorhanden</p>
                  }
                </div>
                <div class="modal-footer">
                  <button class="auth-button back-button" (click)="closePaymentHistory()">
                    Schließen
                  </button>
                </div>
              </div>
            }
          </div>
        }
      </div>
      </div>
    </main>

    <app-footer></app-footer>

    <!-- Toast Notification -->
    @if (toastService.toastMessage()) {
      <div class="toast" [class]="toastService.toastMessage()!.type">
        {{ toastService.toastMessage()!.text }}
      </div>
    }
  `,
  styles: [`
    :host {
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        Helvetica, Arial, sans-serif;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
    }

    main {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0 0 1.5rem;
      min-height: 0;
    }

    .subscription-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 22px 1.5rem 0;
    }

    .header-container {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      background: white;
      color: white;
      padding: 1rem 2rem 1rem 2rem;
      margin: 0;
      border-radius: 0;
      box-shadow: none;
      border-bottom: 1px solid #d1d5db;
      position: relative;
      z-index: 10;
      overflow: visible;
    }

    .header-container::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
      pointer-events: none;
    }

    .back-link {
      position: absolute;
      left: 5px;
      top: 37px;
      z-index: 10;
      pointer-events: none;
    }

    .back-link a {
      pointer-events: auto;
    }

    .back-link a:hover {
      opacity: 0.7;
      text-decoration: underline;
    }

    .back-link a:focus {
      outline: 2px solid oklch(51% 0.28 263.83);
      outline-offset: 2px;
      border-radius: 2px;
    }

    .header-content {
      flex: 1;
      text-align: center;
      position: relative;
      overflow: visible;
    }

    .header-content::before {
      content: '';
      position: absolute;
      top: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 40px;
      background: linear-gradient(135deg, oklch(60% 0.3 280) 0%, oklch(45% 0.28 263.83) 100%);
      border-radius: 50%;
      opacity: 0.1;
      filter: blur(20px);
      z-index: 0;
    }

    .header-content h1 {
      background: linear-gradient(135deg, oklch(45% 0.28 263.83) 0%, oklch(60% 0.3 280) 50%, oklch(45% 0.28 263.83) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-size: 3rem;
      margin: 0;
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
      font-weight: 900;
      letter-spacing: 0px;
      position: absolute;
      top: -32px;
      left: 50%;
      transform: translateX(calc(-50% + 7px));
      white-space: nowrap;
    }

    .header-content p {
      color: oklch(28% 0.06 263.83);
      margin: 43px 0 0 15px !important;
      padding-bottom: 0rem;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 1px;
      text-transform: uppercase;
      line-height: 1.4;
    }

    .subscription-container {
      max-width: 660px !important;
      margin: 0 auto;
      padding: 20px;
      padding-bottom: 20px;
      min-height: auto;
      border: 1px solid #ccc;
      border-radius: 8px;
      background: #f9f9f9;
      width: 100%;
      box-sizing: border-box;
    }

    @media (max-height: 750px) {
      .subscription-container {
        margin: auto;
        padding: 30px;
      }
    }

    .form-title {
      text-align: center;
      padding-top: 5px;
      margin-bottom: 22px;
      color: #333;
      font-size: 1.2rem;
    }

    h2, h3 {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin-bottom: 12px;
      margin-top: 0;
    }

    .info-text {
      text-align: center;
      color: #666;
      margin-bottom: 20px;
      font-size: 14px;
    }

    .subscription-info {
      margin-bottom: 15px;
    }

    .info-section {
      margin-bottom: 12px;
      padding: 12px;
      background: white;
      border: 1px solid #ddd;
      border-radius: 6px;
    }

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      font-size: 14px;
    }

    .info-row:not(:last-child) {
      border-bottom: 1px solid #f0f0f0;
    }

    .label {
      color: #666;
      font-weight: 500;
    }

    .value {
      color: #333;
      text-align: right;
      font-weight: 500;
    }

    .value.status-active {
      color: #2e7d32;
    }

    .value.status-expired {
      color: #d32f2f;
    }

    .value.status-inactive {
      color: #999;
    }

    .value.warning {
      color: #f57c00;
      font-weight: 600;
    }

    .warning-badge {
      margin-left: 8px;
    }

    .plan-selection-row {
      display: flex;
      gap: 10px;
      width: 100%;
      margin-bottom: 15px;
    }

    .plan-dropdown {
      flex: 1;
      padding: 10px 12px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 14px;
      background-color: white;
      cursor: pointer;
      color: #333;
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .plan-dropdown:focus {
      outline: 2px solid oklch(51% 0.28 263.83);
      outline-offset: 0;
    }

    .plan-dropdown:hover {
      border-color: oklch(51% 0.28 263.83);
    }

    .renew-button {
      flex: 0 1 auto;
      white-space: nowrap;
      padding: 10px 20px;
    }

    .auth-button {
      width: 100%;
      padding: 10px;
      border: none;
      border-radius: 4px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.3s;
    }

    .primary-button {
      background-color: #4CAF50;
      color: white;
    }

    .primary-button:hover:not(:disabled) {
      background-color: #45a049;
    }

    .secondary-button {
      background-color: #2196F3;
      color: white;
    }

    .secondary-button:hover:not(:disabled) {
      background-color: #0b7dda;
    }

    .payment-history-link {
      color: oklch(51% 0.28 263.83);
      text-decoration: none;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: all 0.2s ease;
      display: block;
      margin: 35px auto 0;
      width: fit-content;
      padding: 0;
    }

    .payment-history-link:hover {
      color: oklch(45% 0.28 263.83);
      text-decoration: underline;
      text-underline-offset: 4px;
    }

    button:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }

    .payment-history-section {
      margin-bottom: 20px;
      padding: 15px;
      background: white;
      border: 1px solid #ddd;
      border-radius: 6px;
    }

    .billing-data-section {
      margin-bottom: 24px;
      padding: 16px;
      background: #f8f9fa;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
    }

    .accordion-header {
      background: none;
      border: none;
      width: 100%;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 0;
      margin: 0;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      color: #333;
      transition: color 0.2s ease;
    }

    .accordion-header:hover {
      color: oklch(51% 0.28 263.83);
    }

    .accordion-arrow {
      display: inline-block;
      font-size: 16px;
      transition: transform 0.3s ease;
      color: oklch(51% 0.28 263.83);
      flex-shrink: 0;
    }

    .accordion-arrow.open {
      transform: rotate(90deg);
    }

    .billing-display {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .bill-row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      padding: 4px 0;
      border-bottom: 1px solid #e5e7eb;
    }

    .bill-row:last-of-type {
      border-bottom: none;
    }

    .bill-label {
      font-size: 12px;
      font-weight: 600;
      color: oklch(51% 0.28 263.83);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      flex-shrink: 0;
    }

    .bill-value {
      font-size: 15px;
      color: #333;
      font-weight: 500;
      text-align: right;
      flex: 1;
      word-break: break-word;
      overflow-wrap: break-word;
      word-wrap: break-word;
    }

    .accordion-content {
      animation: slideDown 0.3s ease-out;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        max-height: 0;
      }
      to {
        opacity: 1;
        max-height: 500px;
      }
    }

    .billing-form {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .form-group label {
      font-weight: 600;
      font-size: 12px;
      color: #333;
      margin-bottom: 2px;
    }

    .checkbox-group {
      margin-bottom: 12px;
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-weight: 600;
      font-size: 13px;
      color: #333;
    }

    .checkbox-input {
      width: 18px;
      height: 18px;
      cursor: pointer;
      accent-color: #4CAF50;
      margin: 0;
    }

    .checkbox-disabled {
      cursor: not-allowed;
      accent-color: #999;
      background-color: white;
    }

    .checkbox-label:has(.checkbox-disabled) {
      cursor: not-allowed;
    }

    .form-group input {
      padding: 4px 6px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 12px;
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .readonly-input {
      background-color: white;
      border: 1px solid #ddd;
      color: #666;
      cursor: not-allowed;
    }

    .readonly-input:focus {
      outline: none;
      border-color: #ddd;
      box-shadow: none;
    }

    .form-group select {
      padding: 4px 10px;
      padding-left: 3px;
      border: 1px solid #ced4da;
      border-radius: 4px;
      font-size: 12px;
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .form-group input:focus {
      outline: none;
      border-color: oklch(51% 0.28 263.83);
      box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
    }

    .plz-group {
      flex: 0 0 auto !important;
      width: 65px !important;
    }

    .plz-group input {
      width: 100%;
    }

    .stadt-group {
      flex: 0.95 !important;
    }

    .char-counter {
      font-size: 10px;
      color: #999;
      margin-top: 1px;
      text-align: right;
    }

    .char-counter.warning {
      color: #f44336;
      font-weight: 600;
    }

    .form-row {
      display: flex;
      gap: 4px;
    }

    .form-buttons {
      display: flex;
      gap: 8px;
      margin-top: 6px;
    }

    .form-buttons button {
      flex: 1;
      padding: 6px 12px;
      border: none;
      border-radius: 4px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.3s;
    }

    .form-buttons button:disabled {
      background-color: #d1d5db !important;
      color: #9ca3af !important;
      cursor: not-allowed;
      opacity: 0.6;
    }

    /* Validation Helper Box */
    .validation-helper-box {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 8px 10px;
      background: #f0f9ff;
      border: 1px solid #bfdbfe;
      border-radius: 6px;
      margin-bottom: 8px;
      font-size: 12px;
      color: #1e40af;
    }

    .helper-icon {
      font-size: 16px;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .helper-text {
      flex: 1;
      line-height: 1.5;
    }

    /* Conditional Required Badge */
    .conditional-required {
      color: #dc2626;
      font-size: 11px;
      font-weight: 600;
      margin-left: 6px;
      white-space: nowrap;
    }

    /* Optional Label */
    .optional-label {
      color: #999;
      font-size: 11px;
      font-weight: 400;
      margin-left: 4px;
      white-space: nowrap;
    }

    /* Validation Error Box */
    .validation-error-box {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 6px;
      margin-top: 6px;
      margin-bottom: 6px;
      font-size: 12px;
      color: #991b1b;
      animation: slideDown 0.2s ease-out;
    }

    .error-icon {
      font-size: 16px;
      flex-shrink: 0;
    }

    .error-text {
      flex: 1;
    }

    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 200;
      cursor: default;
    }

    .modal-content {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      z-index: 201;
      width: 90%;
      max-width: 720px;
      max-height: 80vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      box-sizing: border-box;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 20px;
      border-bottom: 1px solid #e5e7eb;
      flex-shrink: 0;
    }

    .modal-header h2 {
      margin: 0;
      font-size: 1.25rem;
      color: #333;
    }

    .modal-close {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: #666;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s;
    }

    .modal-close:hover {
      background-color: #f0f0f0;
    }

    .modal-body {
      padding: 20px;
      overflow-y: auto;
      flex: 1;
    }

    .modal-footer {
      padding: 15px 20px;
      border-top: 1px solid #e5e7eb;
      display: flex;
      justify-content: flex-end;
      flex-shrink: 0;
    }

    .payment-table {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .payment-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px;
      background: #f9f9f9;
      border: 1px solid #eee;
      border-radius: 4px;
      gap: 10px;
    }

    .payment-info {
      flex: 1;
    }

    .payment-plan {
      font-weight: 600;
      color: #333;
      font-size: 14px;
    }

    .payment-date {
      font-size: 12px;
      color: #999;
      margin-top: 4px;
    }

    .payment-amount {
      font-weight: 600;
      color: #4CAF50;
      min-width: 80px;
      text-align: right;
    }

    .payment-buttons {
      display: flex;
      gap: 6px;
    }

    .download-button {
      background-color: #2196F3;
      color: white;
      border: none;
      padding: 6px 12px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.2s;
      white-space: nowrap;
    }

    .download-button:hover {
      background-color: #0b7dda;
    }

    .download-button.xml-button {
      background-color: #FF9800;
    }

    .download-button.xml-button:hover {
      background-color: #e68900;
    }

    .back-button {
      background-color: #f3f4f6;
      color: #374151;
      width: auto;
      padding: 0.75rem 1.5rem;
      border-radius: 6px;
      font-size: 1rem;
    }

    .back-button:hover {
      background-color: #e5e7eb;
    }

    @media (max-width: 768px) {
      main {
        padding: 0 0 1rem;
        gap: 0;
      }

      .subscription-scroll {
        padding: 22px 1rem 0;
      }

      .header-content h1 {
        font-size: 2rem;
        padding-top: 1rem;
      }

      .header-content p {
        font-size: 0.5625rem !important;
        margin: 32px 0 0 15px !important;
      }

      .payment-row {
        flex-direction: column;
        align-items: flex-start;
      }

      .payment-amount {
        width: 100%;
        text-align: left;
      }

      .payment-buttons {
        width: 100%;
        display: flex;
        gap: 6px;
      }

      .download-button {
        flex: 1;
        text-align: center;
      }
    }

    /* Toast Styles */
    .toast {
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      background-color: #4CAF50;
      color: white;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      z-index: 1000;
      animation: slideIn 0.3s ease-out;
    }

    .toast.success {
      background-color: #4CAF50;
    }

    .toast.error {
      background-color: #f44336;
    }

    .toast::before {
      content: '';
      display: inline-block;
      width: 20px;
      height: 20px;
      margin-right: 8px;
      vertical-align: middle;
    }

    .toast.success::before {
      content: '✓';
    }

    .toast.error::before {
      content: '✕';
    }

    @keyframes slideIn {
      from {
        transform: translateX(400px);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubscriptionManagementComponent {
  private location = inject(Location);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private subscriptionService = inject(SubscriptionService);
  private countriesService = inject(CountriesService);
  toastService = inject(ToastService);

  subscriptionStatus = signal<SubscriptionStatus | null>(null);
  isLoading = signal(true);
  isCheckoutLoading = signal(false);
  showCheckout = signal(false);
  selectedPlan = signal<any>(null);
  availablePlans = signal<any[]>([]);
  selectedPlanId = signal<number | null>(null);
  paymentDetails = signal<any>(null);
  showPaymentHistory = signal(false);
  paymentHistory = signal<any[]>([]);
  rechnungsdatenOpen = signal(false);
  editingBillingData = signal(false);
  countries = signal<string[]>([]);
  billingDataForm = signal<any>({
    firstName: '',
    lastName: '',
    companyName: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'Deutschland',
    isB2B: false,
    customerVAT: ''
  });

  // Character counters for billing data
  firstNameCount = signal(0);
  lastNameCount = signal(0);
  companyNameCount = signal(0);
  streetCount = signal(0);
  postalCodeCount = signal(0);
  cityCount = signal(0);
  countryCount = signal(0);
  customerVATCount = signal(0);

  // Max lengths
  maxLengths = {
    firstName: 50,
    lastName: 50,
    companyName: 100,
    street: 100,
    postalCode: 16,
    city: 100,
    country: 100,
    customerVAT: 50
  };

  // Validation signals and computed properties
  validationError = computed(() => this.getValidationError());
  isSaveEnabled = computed(() => !this.validationError());
  
  // Computed properties for conditional field display
  isB2B = computed(() => this.subscriptionStatus()?.isB2B || false);
  shouldShowPersonalInfo = computed(() => !this.isB2B());
  shouldShowBusinessInfo = computed(() => this.isB2B());
  
  // Helper text based on B2B or B2C
  helperText = computed(() => {
    if (this.shouldShowPersonalInfo()) {
      return 'Vorname, Nachname, Straße, PLZ, Stadt und Land sind obligatorisch!';
    } else {
      return 'Unternehmensname, USt-Id, Straße, PLZ, Stadt und Land sind obligatorisch!';
    }
  });

  constructor() {
    this.loadSubscriptionStatus();
    this.loadAvailablePlans();
    this.loadCountries();

    // Effekt um aktuellen Plan zu wählen wenn beide loaded sind
    effect(() => {
      const status = this.subscriptionStatus();
      const plans = this.availablePlans();
      if (status && plans.length > 0 && status.planKey) {
        this.selectCurrentPlan();
      }
    });

    // Effekt um Query Parameter zu überwachen und Checkout zu öffnen
    effect(() => {
      this.activatedRoute.queryParams.subscribe(params => {
        if (params['showCheckout'] === 'true') {
          this.showCheckout.set(true);
        }
      });
    });

    // Effekt um ausgewählten Plan aus planId Query Parameter zu restaurieren
    effect(() => {
      this.activatedRoute.queryParams.subscribe(params => {
        if (params['planId']) {
          const planId = parseInt(params['planId']);
          const loadedPlans = this.availablePlans();
          if (loadedPlans.length > 0) {
            const plan = loadedPlans.find(p => p.planId === planId);
            if (plan) {
              this.selectedPlan.set(plan);
              this.selectedPlanId.set(planId);
            }
          }
        }
      });
    });
  }

  private loadAvailablePlans() {
    this.subscriptionService.getAvailablePlans().subscribe({
      next: (plans: any) => {
        // Filter out trial plan for renewal/upgrade scenarios
        const filtered = plans.filter((p: any) => {
          return p.planKey !== 'trial';
        });
        
        this.availablePlans.set(filtered);
      },
      error: (error) => {
        console.error('Fehler beim Laden der Pläne:', error);
      }
    });
  }

  private loadSubscriptionStatus() {
    this.isLoading.set(true);
    this.subscriptionService.getSubscriptionStatus().subscribe({
      next: (status) => {
        this.subscriptionStatus.set(status);
        this.loadPaymentDetails();
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Fehler beim Laden des Abo-Status:', error);
        this.isLoading.set(false);
      }
    });
  }

  private selectCurrentPlan() {
    const status = this.subscriptionStatus();
    const plans = this.availablePlans();
    if (status?.planKey && plans.length > 0) {
      const currentPlan = plans.find(p => p.planKey === status.planKey);
      if (currentPlan) {
        this.selectedPlanId.set(currentPlan.planId);
        this.selectedPlan.set(currentPlan);
      }
    }
  }

  private loadCountries() {
    this.countriesService.getCountries().subscribe({
      next: (countries: string[]) => {
        this.countries.set(countries);
      },
      error: (error) => {
        console.error('Fehler beim Laden der Länder:', error);
      }
    });
  }

  private loadPaymentDetails() {
    this.subscriptionService.getPaymentHistory().subscribe({
      next: (history) => {
        if (history && history.length > 0) {
          this.paymentDetails.set(history[0]);
        }
      },
      error: (error) => {
        console.error('Fehler beim Laden der Zahlungshistorie:', error);
      }
    });
  }

  onViewPaymentHistory() {
    this.subscriptionService.getPaymentHistory().subscribe({
      next: (history) => {
        this.paymentHistory.set(history);
        this.showPaymentHistory.set(true);
      },
      error: (error: any) => {
        console.error('Fehler beim Laden der Zahlungshistorie:', error);
      }
    });
  }

  closePaymentHistory() {
    this.showPaymentHistory.set(false);
    this.editingBillingData.set(false);
    this.rechnungsdatenOpen.set(false);
  }

  async downloadInvoice(payment: any) {
    if (!payment || !payment.paymentId) {
      this.toastService.error('Zahlungs-ID nicht gefunden');
      return;
    }

    // Prüfe ob Rechnungsdaten komplett sind
    if (!this.isBillingDataComplete()) {
      this.toastService.error('Bitte füllen Sie zunächst alle Rechnungsdaten aus. Nach dem Ausfüllen drücken Sie "Speichern".');
      await this.startEditingBillingData();
      this.rechnungsdatenOpen.set(true);
      return;
    }

    // Show loading state
    const button = event?.target as HTMLButtonElement;
    if (button) {
      button.disabled = true;
      button.textContent = '📥 Lädt...';
    }

    // Call backend API
    this.subscriptionService.downloadInvoice(payment.paymentId).subscribe({
      next: (response: any) => {
        // Get blob and headers from response
        const blob = response.body;
        const invoiceNumber = response.headers.get('X-Invoice-Number') || payment.invoiceNumber || payment.paymentId;
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${invoiceNumber}.pdf`;
        link.click();
        
        // Cleanup
        window.URL.revokeObjectURL(url);
        
        // Reset button
        if (button) {
          button.disabled = false;
          button.textContent = '📄 PDF';
        }
      },
      error: (error) => {
        console.error('Fehler beim Download der Rechnung:', error);
        this.toastService.error('Fehler beim Download der Rechnung');
        
        // Reset button
        if (button) {
          button.disabled = false;
          button.textContent = '📄 PDF';
        }
      }
    });
  }

  async downloadXmlInvoice(payment: any) {
    if (!payment || !payment.paymentId) {
      this.toastService.error('Zahlungs-ID nicht gefunden');
      return;
    }

    // Prüfe ob Rechnungsdaten komplett sind
    if (!this.isBillingDataComplete()) {
      this.toastService.error('Bitte füllen Sie zunächst alle Rechnungsdaten aus. Nach dem Ausfüllen drücken Sie "Speichern".');
      await this.startEditingBillingData();
      this.rechnungsdatenOpen.set(true);
      return;
    }

    // Show loading state - find XML button
    const buttons = document.querySelectorAll('.download-button.xml-button');
    let xmlButton: HTMLButtonElement | null = null;
    
    // Try to find the button by searching through parent's buttons
    const eventTarget = event?.target as HTMLElement;
    if (eventTarget?.classList.contains('xml-button')) {
      xmlButton = eventTarget as HTMLButtonElement;
    }
    
    if (xmlButton) {
      xmlButton.disabled = true;
      xmlButton.textContent = '📥 Lädt...';
    }

    // Call backend API
    this.subscriptionService.downloadXmlInvoice(payment.paymentId).subscribe({
      next: (response: any) => {
        // Get blob and headers from response
        const blob = response.body;
        const invoiceNumber = response.headers.get('X-Invoice-Number') || payment.invoiceNumber || payment.paymentId;
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${invoiceNumber}.xml`;
        link.click();
        
        // Cleanup
        window.URL.revokeObjectURL(url);
        
        // Reset button
        if (xmlButton) {
          xmlButton.disabled = false;
          xmlButton.textContent = '📋 XML';
        }
      },
      error: (error) => {
        console.error('Fehler beim Download der E-Rechnung:', error);
        this.toastService.error('Fehler beim Download der E-Rechnung');
        
        // Reset button
        if (xmlButton) {
          xmlButton.disabled = false;
          xmlButton.textContent = '📋 XML';
        }
      }
    });
  }

  goBack() {
    this.location.back();
  }

  goHome() {
    this.router.navigate(['/']);
  }

  onPlanSelected(event: Event) {
    const target = event.target as HTMLSelectElement;
    const planIdString = target.value;
    const planId = planIdString ? parseInt(planIdString) : null;
    
    if (planId) {
      const plan = this.availablePlans().find(p => p.planId === planId);
      if (plan) {
        this.selectedPlanId.set(planId);
        this.selectedPlan.set(plan);
      }
    } else {
      this.selectedPlanId.set(null);
      this.selectedPlan.set(null);
    }
  }

  onRenewSubscription() {
    if (!this.selectedPlanId()) {
      alert('Bitte wähle ein Paket aus');
      return;
    }
    
    this.isCheckoutLoading.set(true);
    // Laden der Pläne ist nicht nötig, da availablePlans schon geladen ist
    this.isCheckoutLoading.set(false);
    
    const plan = this.availablePlans().find(p => p.planId === this.selectedPlanId());
    if (plan) {
      this.selectedPlan.set(plan);
      this.showCheckout.set(true);
      // Setze Query Parameter um Popup-State und Plan-Selection bei Navigation zu erhalten
      this.router.navigate(['/subscription-management'], { queryParams: { showCheckout: 'true', planId: plan.planId }, queryParamsHandling: 'merge', replaceUrl: true });
    }
  }

  closeCheckout() {
    this.showCheckout.set(false);
    this.selectedPlan.set(null);
    this.loadSubscriptionStatus();
    // Entferne Query Parameter um saubere URL zu haben
    this.router.navigate(['/subscription-management'], { queryParams: {}, replaceUrl: true });
  }

  getActiveSince(): string {
    const paymentDetails = this.paymentDetails();
    if (paymentDetails?.createdAt) {
      const date = new Date(paymentDetails.createdAt);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: '2-digit'
      });
    }
    return 'Unbekannt';
  }

  getEndDate(): string {
    const status = this.subscriptionStatus();
    if (status?.endDate) {
      return new Date(status.endDate).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: '2-digit'
      });
    }
    return 'Unbekannt';
  }

  getPaymentDate(dateString: string): string {
    if (!dateString) return 'Unbekannt';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }) + ', ' + date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
  }

  getProviderDisplayName(provider: string): string {
    return provider === 'stripe' ? 'Stripe' : provider === 'paypal' ? 'PayPal' : provider;
  }

  async startEditingBillingData() {
    // Lade die neuesten Daten direkt neu
    try {
      const status = await firstValueFrom(this.subscriptionService.getSubscriptionStatus());
      this.subscriptionStatus.set(status);
    } catch (error) {
      console.error('Fehler beim Laden der Subscription-Daten:', error);
      return;
    }

    const status = this.subscriptionStatus();
    if (!status) return;

    // Stelle sicher, dass die Countries geladen sind
    if (this.countries().length === 0) {
      try {
        const countries = await firstValueFrom(this.countriesService.getCountries());
        this.countries.set(countries);
      } catch (error) {
        console.error('Fehler beim Laden der Länder:', error);
      }
    }

    // Jetzt setzen wir den Form mit den korrekten Werten
    this.billingDataForm.set({
      firstName: status.firstName || '',
      lastName: status.lastName || '',
      companyName: status.companyName || '',
      street: status.street || '',
      postalCode: status.postalCode || '',
      city: status.city || '',
      country: status.country || 'Deutschland',
      isB2B: status.isB2B || false,
      customerVAT: status.customerVAT || ''
    });
    this.updateCharacterCounts();
    this.editingBillingData.set(true);
  }

  cancelEditingBillingData() {
    this.editingBillingData.set(false);
    this.rechnungsdatenOpen.set(false);
    this.billingDataForm.set({
      firstName: '',
      lastName: '',
      companyName: '',
      street: '',
      postalCode: '',
      city: '',
      country: 'Deutschland',
      isB2B: false,
      customerVAT: ''
    });
  }

  updateCharacterCounts() {
    const form = this.billingDataForm();
    this.firstNameCount.set(form.firstName?.length || 0);
    this.lastNameCount.set(form.lastName?.length || 0);
    this.companyNameCount.set(form.companyName?.length || 0);
    this.streetCount.set(form.street?.length || 0);
    this.postalCodeCount.set(form.postalCode?.length || 0);
    this.cityCount.set(form.city?.length || 0);
    this.countryCount.set(form.country?.length || 0);
    this.customerVATCount.set(form.customerVAT?.length || 0);
  }

  /**
   * Update form field and trigger reactive validations
   */
  updateFormField(field: string, event: any) {
    const value = event.target?.value || '';
    const currentForm = this.billingDataForm();
    
    const updatedForm = { ...currentForm, [field]: value };
    this.billingDataForm.set(updatedForm);
    this.updateCharacterCounts();
  }

  /**
   * Validiert die Rechnungsdaten basierend auf dem B2B-Status:
   * B2B (Geschäftliche Nutzung):
   *   - Unternehmensname ist PFLICHT
   *   - USt-Id ist PFLICHT
   * B2C (Private Nutzung):
   *   - Vorname und Nachname sind PFLICHT
   * Beide:
   *   - Straße, PLZ, Stadt, Land sind OBLIGATORISCH
   */
  private isValidBillingData(): boolean {
    const form = this.billingDataForm();
    const isB2B = this.subscriptionStatus()?.isB2B || false;
    
    const hasFirstName = form.firstName?.trim();
    const hasLastName = form.lastName?.trim();
    const hasCompanyName = form.companyName?.trim();
    const hasCustomerVAT = form.customerVAT?.trim();
    const hasStreet = form.street?.trim();
    const hasPostalCode = form.postalCode?.trim();
    const hasCity = form.city?.trim();

    // Adressfelder sind für alle Fälle obligatorisch
    const hasCompleteAddress = hasStreet && hasPostalCode && hasCity;
    if (!hasCompleteAddress) {
      return false;
    }

    // B2B: Firma und USt-Id sind Pflicht
    if (isB2B) {
      return hasCompanyName && hasCustomerVAT;
    }
    
    // B2C: Vorname und Nachname sind Pflicht
    return hasFirstName && hasLastName;
  }

  /**
   * Gibt die Validierungsfehlermeldung zurück je nach B2B Status (null wenn alles valid ist)
   */
  private getValidationError(): string | null {
    const form = this.billingDataForm();
    const isB2B = this.subscriptionStatus()?.isB2B || false;
    
    const hasFirstName = form.firstName?.trim();
    const hasLastName = form.lastName?.trim();
    const hasCompanyName = form.companyName?.trim();
    const hasCustomerVAT = form.customerVAT?.trim();
    const hasStreet = form.street?.trim();
    const hasPostalCode = form.postalCode?.trim();
    const hasCity = form.city?.trim();
    const hasCountry = form.country?.trim();

    // Adressfelder sind für alle obligatorisch
    if (!hasStreet) {
      return 'Bitte geben Sie Straße und Hausnummer ein';
    }

    if (!hasPostalCode) {
      return 'Bitte geben Sie die Postleitzahl ein';
    }

    if (!hasCity) {
      return 'Bitte geben Sie die Stadt ein';
    }

    if (!hasCountry) {
      return 'Bitte wählen Sie ein Land aus';
    }

    // B2B Validierung
    if (isB2B) {
      if (!hasCompanyName) {
        return 'Bitte geben Sie den Unternehmensname ein';
      }
      if (!hasCustomerVAT) {
        return 'Bitte geben Sie die USt-Id ein';
      }
      return null;
    }

    // B2C Validierung
    if (!hasFirstName) {
      return 'Bitte geben Sie Ihren Vornamen ein';
    }

    if (!hasLastName) {
      return 'Bitte geben Sie Ihren Nachnamen ein';
    }

    return null;
  }

  saveBillingData() {
    const form = this.billingDataForm();
    
    // Validierung
    if (!this.isValidBillingData()) {
      const error = this.validationError();
      if (error) {
        this.toastService.error(error);
      }
      return;
    }

    this.subscriptionService.updateBillingData({
      firstName: form.firstName,
      lastName: form.lastName,
      companyName: form.companyName,
      street: form.street,
      postalCode: form.postalCode,
      city: form.city,
      country: form.country,
      isB2B: this.subscriptionStatus()?.isB2B || false,
      customerVAT: form.customerVAT
    }).subscribe({
      next: () => {
        // Subscription status auffrischen um neue Daten zu laden
        this.loadSubscriptionStatus();
        this.editingBillingData.set(false);
        this.rechnungsdatenOpen.set(false);
        this.toastService.success('Rechnungsdaten erfolgreich gespeichert');
      },
      error: (error) => {
        console.error('Fehler beim Speichern der Rechnungsdaten:', error);
        this.toastService.error('Fehler beim Speichern. Bitte versuchen Sie es später erneut.');
      }
    });
  }

  isBillingDataComplete(): boolean {
    const status = this.subscriptionStatus();
    // ENTWEDER (Vorname + Nachname) ODER Unternehmensname
    const hasPersonalName = !!(status?.firstName && status?.lastName);
    const hasCompanyName = !!status?.companyName;
    return hasPersonalName || hasCompanyName;
  }
}
